/**
 * AddressOverrideDialog — P0 SPRINT (Aug 2026)
 *
 * Toont een confirm-dialog wanneer de gebruiker NA klant-autofill één
 * of meer contact-/adresvelden heeft gewijzigd op een shipment/party.
 *
 * Opties:
 *   1. "Alleen deze zending"       → sluit dialog, party blijft override
 *   2. "Ook klantgegevens bijwerken" → PATCH /admin/customers/{id}
 *                                       met de gewijzigde velden
 *   3. "Annuleren"                  → roll-back naar oorspronkelijke data
 *
 * Nooit stille updates op de klantstamkaart. Volledige diff tonen.
 * RBAC: "Ook klantgegevens bijwerken" vereist `customers:edit_contact`
 * op backend niveau — voor medewerkers zonder rechten toont het dialog
 * die knop niet.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, User } from "lucide-react";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";

import { adminApi, formatApiError, useAuth } from "../../context/AuthContext";

const FIELD_LABELS_NL = {
  phone: "Telefoon",
  mobile: "Mobiel",
  whatsapp: "WhatsApp",
  email: "E-mailadres",
  street: "Straat",
  house_number: "Huisnummer",
  house_number_addition: "Toevoeging",
  postal_code: "Postcode",
  city: "Plaats",
  province: "Provincie",
  country: "Land",
};

export default function AddressOverrideDialog({
  open, onOpenChange, customerId, customerName, diffs, onKeepShipmentOnly,
}) {
  const { hasPermission } = useAuth();
  const canEditCustomer = hasPermission("customers:edit_contact")
                       || hasPermission("customers:edit_full");
  const [busy, setBusy] = useState(false);

  const submitCustomerUpdate = async () => {
    if (!customerId || diffs.length === 0) return;
    setBusy(true);
    try {
      const patch = {};
      for (const d of diffs) {
        patch[d.field] = d.new;
      }
      await adminApi.put(`/admin/customers/${customerId}`, patch);
      toast.success("Klantgegevens bijgewerkt");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err) || "Update mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-lg" data-testid="address-override-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-chivo font-bold text-amber-800">
            <AlertTriangle className="w-5 h-5" />
            Wijziging opslaan?
          </DialogTitle>
          <DialogDescription>
            U heeft gegevens gewijzigd na het selecteren van klant{" "}
            <span className="font-semibold">{customerName || "—"}</span>.
            Wilt u deze wijziging alleen voor deze zending opslaan, of ook op
            de klantkaart bijwerken?
          </DialogDescription>
        </DialogHeader>

        <div className="bg-amber-50 border border-amber-200 rounded p-3 space-y-1.5 text-xs">
          <div className="flex items-center gap-1.5 font-bold text-amber-900 mb-1">
            <User className="w-3.5 h-3.5" /> Gewijzigde velden ({diffs.length})
          </div>
          {diffs.map((d) => (
            <div key={d.field} className="grid grid-cols-3 gap-2 items-center border-b border-amber-100 last:border-b-0 pb-1">
              <div className="text-[10px] uppercase font-semibold text-amber-800">
                {FIELD_LABELS_NL[d.field] || d.field}
              </div>
              <div className="text-gray-500 line-through truncate" title={d.old || ""}>
                {d.old || <span className="italic">(leeg)</span>}
              </div>
              <div className="font-mono font-bold text-gray-900 truncate" title={d.new || ""}>
                {d.new}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}
                  data-testid="override-cancel">
            Annuleren
          </Button>
          <Button variant="outline" onClick={() => {
            onKeepShipmentOnly?.();
            onOpenChange(false);
          }} disabled={busy}
                  data-testid="override-shipment-only">
            Alleen deze zending
          </Button>
          {canEditCustomer && (
            <Button onClick={submitCustomerUpdate} disabled={busy}
                    className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                    data-testid="override-update-customer">
              Ook klantgegevens bijwerken
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
